import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import PrintIcon from "@material-ui/icons/Print";
import ApiClient from "utils/ApiClient";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  mill1: "#00897b",
  mill1Light: "#e0f2f1",
  mill2: "#f57c00",
  mill2Light: "#fff3e0",
  sortex1: "#e91e63",
  sortex1Light: "#fce4ec",
  sortex23: "#7b1fa2",
  sortex23Light: "#f3e5f5",
  rejection: "#37474f",
  rejectionLight: "#eceff1",
  brokenBunker: "#bf360c",
  brokenBunkerLight: "#fbe9e7",
  inUse: "#c62828",
  inUseBg: "#ffebee",
  available: "#2e7d32",
  availableBg: "#e8f5e9",
  cardBorder: "#e0e0e0",
  dialogBg: "#f4f6fb",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const F = (size, weight = 400, color = "#37474f", extra = {}) => ({
  fontSize: size,
  fontWeight: weight,
  color,
  fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
  ...extra,
});

function fmtDate() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function fmtKg(n) {
  if (!n || n === "0.00" || n === "0") return null;
  const num = parseFloat(n);
  if (isNaN(num) || num === 0) return null;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 }) + " Kgs";
}

function isInUse(status) {
  return status === "In Use";
}

// ─── StatusPill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const inuse = isInUse(status);
  if (!status) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 7px",
        borderRadius: 20,
        backgroundColor: inuse ? C.inUseBg : C.availableBg,
        border: `1px solid ${inuse ? C.inUse : C.available}`,
        ...F(9, 700, inuse ? C.inUse : C.available, { letterSpacing: 0.3 }),
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          backgroundColor: inuse ? C.inUse : C.available,
          marginRight: 4,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {inuse ? "In Use" : "Available"}
    </span>
  );
}

// ─── BlockTitle ───────────────────────────────────────────────────────────────
function BlockTitle({ label, color }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        margin: "6px 0 5px",
      }}
    >
      <div
        style={{
          width: 4,
          height: 14,
          background: color,
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      <span
        style={F(10, 700, color, {
          textTransform: "uppercase",
          letterSpacing: 0.5,
        })}
      >
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: `${color}25` }} />
    </div>
  );
}

// ─── MillHeader ───────────────────────────────────────────────────────────────
function MillHeader({ label, color, bg }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: bg,
        borderRadius: 6,
        padding: "5px 10px",
        marginBottom: 8,
      }}
    >
      <span style={F(13, 900, color, { letterSpacing: 1, textTransform: "uppercase" })}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1.5, background: `${color}40` }} />
    </div>
  );
}

// ─── BinCard (whitener / silky / tempering) ───────────────────────────────────
function BinCard({ binName, binData, accentColor, lightBg }) {
  const batch = binData?.column_482 || "";
  const variety = binData?.column_488 || "";
  const status = binData?.column_481 || "";
  const inuse = isInUse(status);

  return (
    <div
      style={{
        background: inuse ? lightBg : "#fafafa",
        border: `1.5px solid ${inuse ? accentColor : C.cardBorder}`,
        borderRadius: 6,
        padding: "7px 9px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minHeight: 68,
        position: "relative",
        overflow: "hidden",
        boxShadow: inuse
          ? `0 2px 6px ${accentColor}22`
          : "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accentColor,
          opacity: inuse ? 1 : 0.18,
          borderRadius: "5px 5px 0 0",
        }}
      />
      <span
        style={F(9, 700, "#607d8b", {
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginTop: 3,
        })}
      >
        {binName}
      </span>
      {inuse && (batch || variety) ? (
        <>
          {batch && (
            <span
              style={F(9, 500, "#546e7a", { wordBreak: "break-word", lineHeight: 1.4 })}
            >
              {batch}
            </span>
          )}
          {variety && <span style={F(11, 700, "#1a237e")}>{variety}</span>}
        </>
      ) : (
        <span style={F(11, 300, "#b0bec5", { margin: "auto 0" })}>—</span>
      )}
      <div style={{ marginTop: "auto" }}>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

// ─── BrokenBunkerCard ─────────────────────────────────────────────────────────
// subsheetData rows come from the flat view (81651088, view 170):
//   column_665_664 = Source Variety
//   column_663_664 = Source Batch No
//   column_666_664 = Broken Rice Qty/Weight (Kgs)
function BrokenBunkerCard({ binName, millingData, subsheetData }) {
  const status = millingData?.column_481 || "";
  const inuse = isInUse(status);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span
        style={F(9, 700, "#78909c", {
          textTransform: "uppercase",
          letterSpacing: 0.3,
          textAlign: "center",
        })}
      >
        {binName}
      </span>
      <div
        style={{
          background: inuse ? C.brokenBunkerLight : "#fafafa",
          border: `1.5px solid ${inuse ? C.brokenBunker : C.cardBorder}`,
          borderRadius: 6,
          padding: "6px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minHeight: 72,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: C.brokenBunker,
            opacity: inuse ? 1 : 0.15,
            borderRadius: "5px 5px 0 0",
          }}
        />
        {inuse ? (
          <>
            {subsheetData && subsheetData.length > 0 ? (
              subsheetData.map((sub, i) => (
                <div
                  key={i}
                  style={{
                    borderTop: i > 0 ? `1px solid ${C.brokenBunker}20` : "none",
                    paddingTop: i > 0 ? 4 : 2,
                    marginTop: i > 0 ? 2 : 0,
                  }}
                >
                  <div style={F(10, 700, "#1a237e")}>{sub.column_665_664}</div>
                  <div style={F(8, 500, "#78909c", { wordBreak: "break-word" })}>
                    {sub.column_663_664}
                  </div>
                  {fmtKg(sub.column_666_664) && (
                    <div style={F(8, 600, "#e65100")}>{fmtKg(sub.column_666_664)}</div>
                  )}
                </div>
              ))
            ) : (
              <span style={F(11, 300, "#90a4ae", { margin: "auto", textAlign: "center" })}>
                —
              </span>
            )}
            <div style={{ marginTop: "auto" }}>
              <StatusPill status="In Use" />
            </div>
          </>
        ) : (
          <span
            style={F(12, 300, "#b0bec5", { margin: "auto", textAlign: "center" })}
          >
            —
          </span>
        )}
      </div>
    </div>
  );
}

// ─── SortexMachineCard ────────────────────────────────────────────────────────
function SortexMachineCard({ binName, binData, accentColor, lightBg }) {
  const status = binData?.column_481 || "";
  const inuse = isInUse(status);
  const batch = binData?.column_482 || "";
  const variety = binData?.column_488 || "";
  const source = binData?.column_642 || "";

  return (
    <div
      style={{
        background: inuse ? lightBg : "#f5f7f9",
        border: `2px solid ${inuse ? accentColor : C.cardBorder}`,
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minHeight: 88,
        position: "relative",
        overflow: "hidden",
        boxShadow: inuse
          ? `0 3px 10px ${accentColor}33`
          : "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: accentColor,
          opacity: inuse ? 1 : 0.2,
          borderRadius: "7px 7px 0 0",
        }}
      />
      <span
        style={F(10, 800, accentColor, {
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginTop: 4,
        })}
      >
        {binName}
      </span>
      {inuse ? (
        <>
          {source && <span style={F(9, 600, "#78909c")}>{source}</span>}
          {batch && (
            <span style={F(9, 500, "#546e7a", { wordBreak: "break-word", lineHeight: 1.4 })}>
              {batch}
            </span>
          )}
          {variety && <span style={F(13, 800, "#1a237e")}>{variety}</span>}
        </>
      ) : (
        <span style={F(13, 300, "#b0bec5", { marginTop: "auto" })}>—</span>
      )}
      <div style={{ marginTop: "auto" }}>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

// ─── SortexInputBunkerCard ────────────────────────────────────────────────────
function SortexInputBunkerCard({ binName, binData, accentColor, lightBg }) {
  const status = binData?.column_481 || "";
  const inuse = isInUse(status);
  const variety = binData?.column_488 || "";
  const batch = binData?.column_482 || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={F(9, 700, accentColor, {
          textTransform: "uppercase",
          letterSpacing: 0.4,
          textAlign: "center",
        })}
      >
        {binName}
      </span>
      <div
        style={{
          background: inuse ? lightBg : "#f5f7f9",
          border: `1.5px solid ${inuse ? accentColor : C.cardBorder}`,
          borderRadius: 6,
          clipPath: "polygon(12% 0%, 88% 0%, 78% 100%, 22% 100%)",
          minHeight: 60,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 4px",
          gap: 2,
        }}
      >
        {inuse && (batch || variety) ? (
          <>
            {variety && (
              <span
                style={F(9, 700, "#1a237e", { textAlign: "center", wordBreak: "break-word" })}
              >
                {variety}
              </span>
            )}
            {batch && (
              <span
                style={F(8, 400, "#546e7a", {
                  textAlign: "center",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                })}
              >
                {batch}
              </span>
            )}
          </>
        ) : (
          <span style={F(12, 300, "#b0bec5")}>—</span>
        )}
      </div>
      <div style={{ textAlign: "center" }}>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

// ─── SortexOutputBinCard ──────────────────────────────────────────────────────
// subsheetData rows come from the flat view (80316180, view 178):
//   column_696_697 = Rice Type
//   column_699_697 = Batch Variety
//   column_698_697 = Batch No
//   column_700_697 = Quantity (Kgs)
function SortexOutputBinCard({
  binName,
  masterData,
  subsheetData,
  accentColor,
  lightBg,
  compact,
}) {
  const status = masterData?.column_481 || "";
  const inuse = isInUse(status);
  const rejGrade = masterData?.column_608 || "";

  return (
    <div
      style={{
        background: inuse ? lightBg : "#fafafa",
        border: `1.5px solid ${inuse ? accentColor : C.cardBorder}`,
        borderRadius: 6,
        padding: "5px 8px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: compact ? 56 : 66,
        position: "relative",
        overflow: "hidden",
        boxShadow: inuse ? `0 2px 5px ${accentColor}22` : "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accentColor,
          opacity: inuse ? 1 : 0.12,
          borderRadius: "5px 5px 0 0",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 3,
        }}
      >
        <span style={F(11, 800, accentColor, { letterSpacing: 0.3 })}>
          {binName}
        </span>
        {rejGrade && inuse && (
          <span
            style={F(8, 700, "#546e7a", {
              background: "#eceff1",
              padding: "1px 5px",
              borderRadius: 4,
            })}
          >
            {rejGrade}
          </span>
        )}
      </div>
      {inuse ? (
        <>
          {subsheetData && subsheetData.length > 0 ? (
            subsheetData.map((sub, i) => (
              <div
                key={i}
                style={{
                  borderTop: i > 0 ? `1px solid ${accentColor}20` : "none",
                  paddingTop: i > 0 ? 3 : 0,
                }}
              >
                <span style={F(8, 600, "#78909c")}>{sub.column_696_697} </span>
                <span style={F(9, 700, "#1a237e")}>{sub.column_699_697}</span>
                <div
                  style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}
                >
                  <span style={F(8, 400, "#90a4ae", { wordBreak: "break-word" })}>
                    {sub.column_698_697}
                  </span>
                  {fmtKg(sub.column_700_697) && (
                    <span style={F(8, 600, "#e65100")}>{fmtKg(sub.column_700_697)}</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <span style={F(11, 300, "#90a4ae", { margin: "auto 0" })}>—</span>
          )}
          <div style={{ marginTop: "auto" }}>
            <StatusPill status="In Use" />
          </div>
        </>
      ) : (
        <span style={F(11, 300, "#b0bec5", { margin: "auto 0" })}>—</span>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function DPARMillingSortexReport({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [millingMap, setMillingMap] = useState({});     // binName.lower → row (main sheet)
  const [brokenSubMap, setBrokenSubMap] = useState({});  // binName.lower → subsheet rows[]
  const [sortexSubMap, setSortexSubMap] = useState({});  // binName.lower → subsheet rows[]
  const [reportDate, setReportDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReportDate(fmtDate());

      // Always fire all 3 APIs in parallel — no conditionals needed
      const [mainResult, brokenResult, sortexResult] = await Promise.allSettled([
        // 1. Milling & Sorter Bin Master — bin statuses, batch, variety
        ApiClient.Views.getViewData({
          list_id: 82064112,
          view_id: 123,
          limit: 1000,
        }),
        // 2. Broken bunker flat subsheet view — all broken rice entries keyed by bin name
        ApiClient.Views.getViewData({
          list_id: 81651088,
          view_id: 170,
          limit: 100000,
        }),
        // 3. Sortex output flat subsheet view — all final rice entries keyed by bin name
        ApiClient.Views.getViewData({
          list_id: 80316180,
          view_id: 178,
          limit: 100000,
        }),
      ]);

      // ── Main sheet: build binName → row map ──────────────────────────────
      if (mainResult.status === "fulfilled" && mainResult.value?.data?.data) {
        const map = {};
        mainResult.value.data.data.forEach((row) => {
          const key = (row.column_479 || "").trim().toLowerCase();
          map[key] = row;
        });
        setMillingMap(map);
      } else {
        console.warn("Main milling sheet fetch failed:", mainResult.reason);
        if (mainResult.status === "rejected") {
          throw new Error("Failed to load main bin data");
        }
      }

      // ── Broken bunker subsheet: group by bin name (column_659) ───────────
      if (brokenResult.status === "fulfilled" && brokenResult.value?.data?.data) {
        const map = {};
        brokenResult.value.data.data.forEach((row) => {
          const key = (row.column_659 || "").trim().toLowerCase();
          if (!map[key]) map[key] = [];
          map[key].push(row);
        });
        setBrokenSubMap(map);
      } else if (brokenResult.status === "rejected") {
        console.warn("Broken bunker subsheet view failed:", brokenResult.reason);
      }

      // ── Sortex output subsheet: group by bin name (column_691) ───────────
      if (sortexResult.status === "fulfilled" && sortexResult.value?.data?.data) {
        const map = {};
        sortexResult.value.data.data.forEach((row) => {
          const key = (row.column_691 || "").trim().toLowerCase();
          if (!map[key]) map[key] = [];
          map[key].push(row);
        });
        setSortexSubMap(map);
      } else if (sortexResult.status === "rejected") {
        console.warn("Sortex output subsheet view failed:", sortexResult.reason);
      }
    } catch (err) {
      console.error("DPAR2 fetch error:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  // ── Lookup helpers ──────────────────────────────────────────────────────────
  const getBin = (name) =>
    millingMap[(name || "").trim().toLowerCase()] || null;

  const getBrokenSub = (binName) =>
    brokenSubMap[(binName || "").trim().toLowerCase()] || [];

  const getSortexSub = (binName) =>
    sortexSubMap[(binName || "").trim().toLowerCase()] || [];

  // ── Mill sub-section renderer ───────────────────────────────────────────────
  const renderMill = (
    mill,
    tempKeys,
    tempLabels,
    brokenKeys,
    brokenLabels,
    accentColor,
    lightBg
  ) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <BinCard
        binName={`Whitener Machine ${mill}`}
        binData={getBin(`whitener machine ${mill}`)}
        accentColor={accentColor}
        lightBg={lightBg}
      />
      <BinCard
        binName={`Silky Machine ${mill}`}
        binData={getBin(`silky machine ${mill}`)}
        accentColor={accentColor}
        lightBg={lightBg}
      />
      <BlockTitle label="Tempering Bins" color={accentColor} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
        {tempKeys.map((key, i) => (
          <BinCard
            key={key}
            binName={`Tempering ${tempLabels[i]}`}
            binData={getBin(key)}
            accentColor={accentColor}
            lightBg={lightBg}
          />
        ))}
      </div>
      <BlockTitle label="Broken Bunkers" color={C.brokenBunker} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
        {brokenKeys.map((key, i) => (
          <BrokenBunkerCard
            key={key}
            binName={brokenLabels[i]}
            millingData={getBin(key)}
            subsheetData={getBrokenSub(key)}
          />
        ))}
      </div>
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          background: C.dialogBg,
          borderRadius: 12,
          maxHeight: "96vh",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <style>{`
        @media print {
          .dpar2-no-print { display: none !important; }
          .MuiDialog-root .MuiBackdrop-root { display: none !important; }
          .MuiDialog-paper {
            box-shadow: none !important; border-radius: 0 !important;
            max-height: none !important; margin: 0 !important;
            width: 100% !important; max-width: 100% !important;
          }
          .MuiDialogContent-root { overflow: visible !important; }
        }
      `}</style>

      {/* ── Title Bar ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 55%, #388e3c 100%)",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "12px 12px 0 0",
          boxShadow: "0 2px 8px rgba(27,94,32,0.25)",
        }}
      >
        <div>
          <div
            style={F(20, 800, "#fff", {
              letterSpacing: 0.5,
              textShadow: "0 1px 3px rgba(0,0,0,0.2)",
            })}
          >
            Daily Production Activity Report{" "}
            <span style={{ color: "#a5d6a7" }}>[DPAR 2]</span>
          </div>
          <div style={F(12, 400, "rgba(255,255,255,0.75)", { marginTop: 3 })}>
            MS Agro Industries &nbsp;—&nbsp; Milling &amp; Sorting &nbsp;|&nbsp;{" "}
            {reportDate || fmtDate()}
          </div>
        </div>
        <div
          className="dpar2-no-print"
          style={{ display: "flex", alignItems: "center", gap: 4 }}
        >
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              style={{ color: "rgba(255,255,255,0.85)" }}
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton
              size="small"
              style={{ color: "rgba(255,255,255,0.85)" }}
              onClick={() => window.print()}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              size="small"
              style={{ color: "rgba(255,255,255,0.85)" }}
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Body ── */}
      <DialogContent style={{ padding: 14, overflow: "auto" }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 16,
            }}
          >
            <CircularProgress style={{ color: "#2e7d32" }} />
            <span style={F(13, 400, "#607d8b")}>
              Loading milling &amp; sorting data…
            </span>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 40, ...F(14, 500, "#d32f2f") }}>
            {error}
          </div>
        ) : (
          /* ── 2-column master grid ── */
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "290px 1fr",
              gap: 12,
              minWidth: 1080,
            }}
          >
            {/* ╔══════════════════════════════════╗
                ║  COL 1: MILLING SECTION          ║
                ╚══════════════════════════════════╝ */}
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid #e3eaf2",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(90deg, #c8e6c9, #dcedc8)",
                  padding: "10px 14px",
                  borderBottom: "2px solid #81c784",
                }}
              >
                <span
                  style={F(12, 800, "#1b5e20", {
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  })}
                >
                  Milling Section
                </span>
              </div>
              <div
                style={{
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {/* ── Mill 1 ── */}
                <MillHeader label="Mill 1" color={C.mill1} bg="#e0f2f1" />
                {renderMill(
                  "1",
                  ["tempering bin 1a", "tempering bin 1b", "tempering bin 1c", "tempering bin 1d"],
                  ["1A", "1B", "1C", "1D"],
                  ["broken bunker 1a", "broken bunker 2a", "broken bunker 3a"],
                  ["BB 1A", "BB 2A", "BB 3A"],
                  C.mill1,
                  C.mill1Light
                )}

                <div style={{ height: 1, background: "#e0e0e0", margin: "4px 0" }} />

                {/* ── Mill 2 ── */}
                <MillHeader label="Mill 2" color={C.mill2} bg="#fff3e0" />
                {renderMill(
                  "2",
                  ["tempering bin 2a", "tempering bin 2b", "tempering bin 2c", "tempering bin 2d"],
                  ["2A", "2B", "2C", "2D"],
                  ["broken bunker 1b", "broken bunker 2b", "broken bunker 3b"],
                  ["BB 1B", "BB 2B", "BB 3B"],
                  C.mill2,
                  C.mill2Light
                )}
              </div>
            </div>

            {/* ╔══════════════════════════════════════════════════════════╗
                ║  COL 2: SORTEX SECTION                                   ║
                ╚══════════════════════════════════════════════════════════╝ */}
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid #e3eaf2",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(90deg, #fce4ec, #f3e5f5)",
                  padding: "10px 14px",
                  borderBottom: "2px solid #f48fb1",
                }}
              >
                <span
                  style={F(12, 800, "#880e4f", {
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  })}
                >
                  Sortex Section
                </span>
              </div>

              <div style={{ padding: "10px 12px" }}>
                {/* Inner 3-column: Sortex1 | Sortex2+3 | Rejection Bins */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1.5fr 155px",
                    gap: 14,
                    alignItems: "start",
                  }}
                >
                  {/* ── Sortex 1 ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div
                      style={{
                        background: "#fce4ec",
                        borderRadius: 6,
                        padding: "5px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={F(12, 900, C.sortex1, {
                          letterSpacing: 1,
                          textTransform: "uppercase",
                        })}
                      >
                        Sortex 1
                      </span>
                      <div style={{ flex: 1, height: 1.5, background: `${C.sortex1}30` }} />
                    </div>

                    <div>
                      <BlockTitle label="Input Bunkers" color={C.sortex1} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        <SortexInputBunkerCard
                          binName="SA-1"
                          binData={getBin("sa-1")}
                          accentColor={C.sortex1}
                          lightBg={C.sortex1Light}
                        />
                        <SortexInputBunkerCard
                          binName="SB-1"
                          binData={getBin("sb-1")}
                          accentColor={C.sortex1}
                          lightBg={C.sortex1Light}
                        />
                      </div>
                    </div>

                    <SortexMachineCard
                      binName="Sortex Machine 1"
                      binData={getBin("sortex machine 1")}
                      accentColor={C.sortex1}
                      lightBg={C.sortex1Light}
                    />

                    <div>
                      <BlockTitle label="Output Bins" color={C.sortex1} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        {["a1", "b1", "c1", "d1", "e1", "f1"].map((key) => (
                          <SortexOutputBinCard
                            key={key}
                            binName={key.toUpperCase()}
                            masterData={getBin(key)}
                            subsheetData={getSortexSub(key)}
                            accentColor={C.sortex1}
                            lightBg={C.sortex1Light}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── Sortex 2+3 ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div
                      style={{
                        background: "#f3e5f5",
                        borderRadius: 6,
                        padding: "5px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={F(12, 900, C.sortex23, {
                          letterSpacing: 1,
                          textTransform: "uppercase",
                        })}
                      >
                        Sortex 2+3
                      </span>
                      <div style={{ flex: 1, height: 1.5, background: `${C.sortex23}30` }} />
                    </div>

                    <div>
                      <BlockTitle label="Input Bunkers" color={C.sortex23} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        <SortexInputBunkerCard
                          binName="SA-2"
                          binData={getBin("sa-2")}
                          accentColor={C.sortex23}
                          lightBg={C.sortex23Light}
                        />
                        <SortexInputBunkerCard
                          binName="SB-2"
                          binData={getBin("sb-2")}
                          accentColor={C.sortex23}
                          lightBg={C.sortex23Light}
                        />
                      </div>
                    </div>

                    <SortexMachineCard
                      binName="Sortex Machine 2+3"
                      binData={getBin("sortex machine 2+3")}
                      accentColor={C.sortex23}
                      lightBg={C.sortex23Light}
                    />

                    <div>
                      <BlockTitle label="Output Bins" color={C.sortex23} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                        {["a2", "b2", "c2", "d2", "e2", "f2"].map((key) => (
                          <SortexOutputBinCard
                            key={key}
                            binName={key.toUpperCase()}
                            masterData={getBin(key)}
                            subsheetData={getSortexSub(key)}
                            accentColor={C.sortex23}
                            lightBg={C.sortex23Light}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── Rejection Bins A3–J3 ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div
                      style={{
                        background: "#eceff1",
                        borderRadius: 6,
                        padding: "5px 10px",
                      }}
                    >
                      <span
                        style={F(10, 800, C.rejection, {
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        })}
                      >
                        Rejection Bins
                      </span>
                    </div>
                    <BlockTitle label="Sortex 2+3" color={C.rejection} />
                    {["a3", "b3", "c3", "d3", "e3", "f3", "g3", "h3", "i3", "j3"].map(
                      (key) => (
                        <SortexOutputBinCard
                          key={key}
                          binName={key.toUpperCase()}
                          masterData={getBin(key)}
                          subsheetData={getSortexSub(key)}
                          accentColor={C.rejection}
                          lightBg={C.rejectionLight}
                          compact
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DPARMillingSortexReport;
